import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Stack, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
  InputAdornment, CircularProgress, Avatar
} from "@mui/material";
import { Add, Edit, Delete, Search, CloudUpload, Receipt } from "@mui/icons-material";
import { 
  collection, query, where, getDocs, addDoc, updateDoc, 
  deleteDoc, doc, Timestamp, orderBy, 
  getDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../../firebase";
import Swal from "sweetalert2";

function Expense() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schoolId, setSchoolId] = useState(null);
  
  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Dialog State
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    amount: "", date: new Date().toISOString().split('T')[0],
    description: "", personName: "", imageUrl: ""
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { getInitialData(); }, []);

  const getInitialData = async () => {
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const sId = userSnap.data().schoolId;
      setSchoolId(sId);
      fetchExpenses(sId);
    } catch (e) { console.error(e); }
  };

  const fetchExpenses = async (sId) => {
    setLoading(true);
    try {
      const q = query(collection(db, "expenses"), where("schoolId", "==", sId), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleFileUpload = async () => {
    if (!file) return formData.imageUrl;
    setUploading(true);
    const storageRef = ref(storage, `expenses/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    setUploading(false);
    return url;
  };

  const handleSubmit = async () => {
    if (!formData.amount || !formData.personName) return Swal.fire("Error", "Fill required fields", "error");
    
    try {
      setUploading(true);
      const imageUrl = await handleFileUpload();
      const finalData = {
        ...formData,
        amount: formData.amount.toString(),
        imageUrl: imageUrl || "",
        schoolId,
        createdAt: Timestamp.now()
      };

      if (editId) {
        await updateDoc(doc(db, "expenses", editId), finalData);
      } else {
        await addDoc(collection(db, "expenses"), finalData);
      }

      Swal.fire("Success", "Expense saved", "success");
      setOpen(false);
      resetForm();
      fetchExpenses(schoolId);
    } catch (e) { Swal.fire("Error", e.message, "error"); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    const res = await Swal.fire({ title: 'Are you sure?', showCancelButton: true, confirmButtonText: 'Delete' });
    if (res.isConfirmed) {
      await deleteDoc(doc(db, "expenses", id));
      fetchExpenses(schoolId);
    }
  };

  const resetForm = () => {
    setFormData({ amount: "", date: new Date().toISOString().split('T')[0], description: "", personName: "", imageUrl: "" });
    setEditId(null);
    setFile(null);
  };

  const filteredExpenses = expenses.filter(exp => 
    exp.personName.toLowerCase().includes(search.toLowerCase()) || 
    exp.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box p={1}>
      <Stack direction="row" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={700}>Expense Manager</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { resetForm(); setOpen(true); }}>Add Expense</Button>
      </Stack>

      <Card sx={{ p: 2, mb: 3 }}>
        <TextField 
          fullWidth placeholder="Search by person or description..." 
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              <TableCell>Sr No</TableCell>
              <TableCell>Photo</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Person</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredExpenses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((exp, index) => (
              <TableRow key={exp.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>
                  {exp.imageUrl ? <Avatar src={exp.imageUrl} variant="rounded" sx={{ cursor: 'pointer' }} onClick={() => window.open(exp.imageUrl)}/> : <Receipt color="disabled"/>}
                </TableCell>
                <TableCell>{exp.date}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{exp.personName}</TableCell>
                <TableCell>{exp.description}</TableCell>
                <TableCell>₹{exp.amount}</TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => { setEditId(exp.id); setFormData(exp); setOpen(true); }} color="primary"><Edit /></IconButton>
                  <IconButton onClick={() => handleDelete(exp.id)} color="error"><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredExpenses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Update Expense" : "Add New Expense"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Person Name" fullWidth value={formData.personName} onChange={e => setFormData({...formData, personName: e.target.value})}/>
            <TextField label="Amount" type="number" fullWidth value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})}/>
            <TextField label="Date" type="date" fullWidth value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} InputLabelProps={{ shrink: true }}/>
            <TextField label="Description" multiline rows={2} fullWidth value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}/>
            
            <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
              {file ? file.name : "Upload Receipt (Optional)"}
              <input type="file" hidden onChange={e => setFile(e.target.files[0])} />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={uploading}>
            {uploading ? <CircularProgress size={24} /> : "Save Expense"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Expense;